<template>
  <patient-profile>
    <div
      class="patient-profile__visits__filter"
      v-if="!isEmptyVisitList || !isEmptyFilters"
    >
      <input-teeth title-hidden v-model="searchTeeth" />
      <input-text-search
        class="patient-profile__visits__filter__field-input"
        v-model="searchService"
        title="Поиск по названию услуги"
        name="service-input"
        placeholder="Название услуги"
      />
    </div>
    <div class="patient-profile__visits">
      <div
        v-if="isEmptyVisitList && !isAwaitRequest"
        class="patient-profile__visits__info"
      >
        У пациента нет состоявшихся визитов. Для создания визита запишите пациента на прием в
        <router-link
          class="patient-profile__visits__link-schedule"
          v-bind:to="{ name: 'ClinicPanelAppointmentScheduleEmployees' }">
          расписании
        </router-link>
        и установите статус "Пришел". Необходимо для корректного формирования отчетов.
      </div>
      <table v-if="!isEmptyVisitList" class="patient-profile__visits__table">
        <thead class="patient-profile__visits__table__head">
          <tr>
            <template v-for="{ actions, fieldKey, title } in visibleColumns">
              <template v-if="actions.includes('sort')">
                <th
                  v-bind:key="fieldKey"
                  v-bind:class="getClassCell(fieldKey)"
                >
                  <input-sort
                    v-bind:name="fieldKey"
                    v-on:input="sortVisitList(fieldKey, $event)"
                  >
                    <div class="patient-profile__visits__table__cell sort">
                      {{ title }}
                    </div>
                  </input-sort>
                </th>
              </template>
              <th
                v-else
                v-bind:key="fieldKey"
                v-bind:class="getClassCell(fieldKey)"
                class="patient-profile__visits__table__cell">
                {{ title }}
              </th>
            </template>
            <popover-menu>
              <template v-slot:trigger="{ listenerObject }">
                <th class="patient-profile__visits__table__cell">
                  <button
                    v-on="listenerObject"
                    class="patient-profile__visits__icon-settings"
                    type="button"
                  />
                </th>
              </template>
              <form-table-settings
                v-bind:columns="tableColumnSettings"
                v-on:change="onChangeColumnSettings"
              />
            </popover-menu>
          </tr>
        </thead>
        <tbody class="patient-profile__visits__table__body">
          <router-link
            v-for="(row, key) in sortedVisitList"
            v-bind:key="key"
            class="patient-profile__visits__table__row"
            v-bind:to="{ name: 'ClinicPanelPatientVisit', params: { id: patientId, visitId: row.id } }"
          >
            <template v-for="{ fieldKey, title } in visibleColumns">
              <td
                v-if="fieldKey === 'serviceNames'"
                v-bind:key="fieldKey"
                class="patient-profile__visits__table__cell service-name"
                v-bind:data-label="title">
                <ul v-if="row.serviceNames.length">
                  <li
                    v-for="(serviceName, key) in row.serviceNames"
                    v-bind:key="key">{{ serviceName }}</li>
                </ul>
              </td>
              <td
                v-else-if="fieldKey === 'iemkStatus'"
                v-bind:key="fieldKey"
                class="patient-profile__visits__table__cell status"
                data-label="ИЭМК"
                v-bind:class="row.iemkStatus.getId()"
                v-bind:title="row.iemkStatus.getText()">
                {{ row.iemkStatus.getTitle() }}
              </td>
              <td
                v-else-if="fieldKey === 'chiefPhysicianStatus'"
                v-bind:key="fieldKey"
                class="patient-profile__visits__table__cell status position-mob"
                v-bind:data-label="title"
                v-bind:class="row.chiefPhysicianStatus.getId()"
                v-bind:title="row.iemkStatus.getText()">
                {{ row.chiefPhysicianStatus.getTitle() }}
              </td>
              <td
                v-else-if="fieldKey === 'employeeFullName'"
                v-bind:key="fieldKey"
                data-label="Врач"
                class="patient-profile__visits__table__cell"
              >
                <template v-if="row.employees.length === 1">
                  {{ row.employees[0].name }}
                </template>
                <ul v-else>
                  <li v-for="(employee, index) in row.employees" v-bind:key="`${fieldKey}-${row.id}-${index}`">
                    {{ employee.name }}
                  </li>
                </ul>
              </td>
              <td
                v-else-if="fieldKey === 'employeeSpeciality'"
                v-bind:key="fieldKey"
                data-label="Специализация"
                class="patient-profile__visits__table__cell"
              >
                <template v-if="row.employees.length === 1">
                  {{ row.employees[0].speciality }}
                </template>
                <ul v-else>
                  <li v-for="(employee, index) in row.employees" v-bind:key="`${fieldKey}-${row.id}-${index}`">
                    {{ employee.speciality || '-' }}
                  </li>
                </ul>
              </td>
              <td
                v-else
                v-bind:key="fieldKey"
                class="patient-profile__visits__table__cell"
                v-bind:data-label="title">{{ row[fieldKey] }}
              </td>
            </template>
          </router-link>
        </tbody>
      </table>
    </div>
  </patient-profile>
</template>

<script>
import notifierMixin from '@/mixins/notifierMixin';
import 'vue-slick-carousel/dist/vue-slick-carousel.css';
import 'vue-slick-carousel/dist/vue-slick-carousel-theme.css';
import InputSort from '@/components/InputSort.vue';
import PatientProfile from '@/views/ClinicPanel/PatientProfile.vue';
import InputTextSearch from '@/components/InputTextSearch.vue';
import InputTeeth from '@/components/InputTeeth.vue';
import PopoverMenu from '@/components/PopoverMenu.vue';
import FormTableSettings from '@/components/FormTableSettings.vue';
import customizableTableMixin from '@/mixins/customizableTableMixin';

/**
 * @description Идентификатор таблицы.
 * @type {string}
 */
const TABLE_ID = 'visits';

/**
 * @description Допустимые ключи столбцов таблицы визитов
 */
const NameColumnTableVisits = {
  DATETITLE: 'dateTitle',
  VISITTIME: 'visitTime',
  BRANCHNAME: 'branchName',
  EMPLOYEE_FULL_NAME: 'employeeFullName',
  EMPLOYEE_SPECIALITY: 'employeeSpeciality',
  SERVICENAMES: 'serviceNames',
  MONEY_TITLE: 'moneyTitle',
  DEBT: 'debt',
};

export default {
  components: {
    InputTeeth,
    InputTextSearch,
    PatientProfile,
    InputSort,
    PopoverMenu,
    FormTableSettings,
  },
  async created() {
    this.fetchTableSettings(TABLE_ID);
    await this.reloadVisitList();
  },
  data() {
    return {
      /**
       * @description Колонки таблицы.
       * @type {Array}
       */
      tableColumns: [],
      /**
       * @description Отсортированный список визитов.
       * @type {Array}
       */
      sortedVisitList: [],
      /**
       * @description Список визитов.
       * @type {any[]}
       */
      visitList: [],
      /**
       * @description Список визитов пуст.
       * @type {boolean}
       */
      isEmptyVisitList: true,
      /**
       * @description Выполняется запрос.
       * @type {boolean}
       */
      isAwaitRequest: false,
      /**
       * @description Строка поиска услуг.
       * @type {string}
       */
      searchService: '',
      /**
       * @description Строка поиска зуба.
       * @type {string}
       */
      searchTeeth: '',
    };
  },
  computed: {
    /**
     * @description Пусты ли фильтры.
     *
     * @returns {boolean}
     */
    isEmptyFilters() {
      return this.searchTeeth.length === 0 && this.searchService.length === 0;
    },
    /**
     * @description Видимые колонки.
     * @returns {any[]}
     */
    visibleColumns() {
      return this.tableColumns.filter((candidate) => candidate.isVisible);
    },
  },
  methods: {
    /**
     * @description Перезагрузить список визитов пациента.
     * @returns {void}
     */
    async reloadVisitList() {
      const { patientId, searchTeeth, searchService } = this;
      if (Number.isNaN(patientId)) {
        return;
      }
      this.isAwaitRequest = true;
      const either = await this.$visitGateway.viewList(patientId, searchService, searchTeeth);
      either.forEach(({ visits, columns }) => {
        this.visitList = visits;
        this.isEmptyVisitList = this.visitList.length === 0;
        this.sortedVisitList = [...visits];
        this.tableColumns = columns.filter(({ fieldKey }) => fieldKey !== 'iemkStatus');
      });
      either.forEachLeft((error) => {
        this.notifyFail(error.title);
      });
      this.isAwaitRequest = false;
    },
    /**
     * Сортировать список визитов.
     * @param {string} sortType
     * @param {string} direction
     * @returns {void}
     */
    sortVisitList(sortType, direction) {
      if (direction === 'desc' || direction === 'asc') {
        const toSortValue = {
          dateTitle: ({ date }) => date.split('.').reverse().join(),
          moneyTitle: ({ moneyValue }) => moneyValue,
        }[sortType] || ((source) => source[sortType]);
        const compare = (first, second) => {
          const firstValue = toSortValue(first);
          const secondValue = toSortValue(second);
          if (firstValue < secondValue) {
            return -1;
          }
          if (firstValue > secondValue) {
            return 1;
          }
          return 0;
        };
        const directionalCompare = direction === 'desc'
          ? (right, left) => compare(left, right)
          : (left, right) => compare(left, right);
        this.sortedVisitList = this.sortedVisitList.sort(directionalCompare);
      } else {
        this.sortedVisitList = [...this.visitList];
      }
    },
    /**
     * @description Вернуть соответствующий класс для столбца.
     * @param {string} fieldKey
     * @returns {string}
     */
    getClassCell(fieldKey) {
      switch (fieldKey) {
        case NameColumnTableVisits.DATETITLE: return 'date';
        case NameColumnTableVisits.VISITTIME: return 'time';
        case NameColumnTableVisits.BRANCHNAME: return 'filial';
        case NameColumnTableVisits.EMPLOYEE_FULL_NAME: return 'fullname';
        case NameColumnTableVisits.EMPLOYEE_SPECIALITY: return 'speciality';
        case NameColumnTableVisits.SERVICENAMES: return 'service-name';
        case NameColumnTableVisits.MONEY_TITLE: return 'money-title';
        case NameColumnTableVisits.DEBT: return 'debt';
        default: return '';
      }
    },
    /**
     * @description Обработать изменение настроек столбцов таблицы.
     * @param {any[]}  columns
     * @returns {void}
     */
    onChangeColumnSettings(columns) {
      this.tableColumnSettings = columns;
      this.tableColumns = this.tableColumns.map((candidate) => {
        const column = columns.find(({ id }) => candidate.id === id);
        if (column) {
          return { ...candidate, isVisible: column.is_visible };
        }
        return candidate;
      });
      this.storeTableSettings(TABLE_ID);
    },
  },
  mixins: [
    customizableTableMixin,
    notifierMixin,
  ],
  name: 'ClinicPanelPatientVisits',
  props: {
    /**
     * @description Идентификатор пациента.
     * @type {number}
     */
    patientId: {
      required: true,
      type: Number,
    },
  },
  watch: {
    searchService: {
      handler() {
        this.reloadVisitList();
      },
    },
    searchTeeth: {
      handler() {
        this.reloadVisitList();
      },
    },
  },
};
</script>

<style lang="scss" scoped>
$switch-width: 16px;
.patient {
  &-profile {
    &__visits {
      width: 100%;
      height: 100%;
      &__info {
        margin-block: 27px;
      }
      &__link-schedule {
        &:hover {
          text-decoration: none;
        }
      }
      &__table {
        width: 100%;
        margin: 27px 0;
        border-collapse: collapse;
        overflow-x: auto;
        &__head,
        &__row {
          text-align: left;
          vertical-align: middle;
          font-size: 14px;
          line-height: 17px;
        }
        &__head {
          font-weight: 700;
          border-bottom: 2px solid #0489e0;
          label {
            cursor: pointer;
          }
          .date,
          .time {
            width: 140px;
          }
          .filial {
            min-width: 160px;
            width: 10%;
          }
          .fullname {
            min-width: 115px;
          }
          .debt,
          .money-title {
            min-width: 115px;
            width: 7%;
          }
        }
        &__row {
          display: table-row;
          border-bottom: 1px solid #eaeff3;
          text-decoration: none;
          cursor: pointer;
          &:hover {
            background: #eaeff3;
          }
        }
        &__cell {
          padding: 12px 8px;
          color: #323C47;
          &.sort {
            text-align: left;
            cursor: pointer;
            display: flex;
            align-items: baseline;
            &:after {
              content: '';
              background-image: url("@/assets/img/checked/checked-grey.svg");
              background-repeat: no-repeat;
              background-size: contain;
              background-position: center;
              width: 10px;
              height: 10px;
              margin: 0 0 0 10px;
            }
            &:hover {
              text-decoration: none;
            }
          }
          &.success,
          &.fail,
          &.none {
            display: flex;
            align-items: center;
            height: 100%;
          }
          &.success:before,
          &.fail:before,
          &.none:before {
            content: '';
            height: 20px;
            margin-right: 10px;
            width: 20px;
            background-repeat: no-repeat;
            background-position: center;
            background-size: contain;
          }
          &.success {
            background: #E4FFEC !important;
            &:before {
              background-image: url("@/assets/img/checked/all-checked-green.svg");
            }
          }
          &.fail {
            background: #FFECEC;
            &:before {
              background-image: url("@/assets/img/exit-red.svg");
            }
          }
          &.none:before {
            height: 6px;
            width: 6px;
            background: #ABB1A3;
            border-radius: 50%;
            margin: 0 10px 0 6px;
          }
        }
      }
      &__filter {
        display: grid;
        grid-template-columns: 0.25fr 1fr;
        align-items: end;
        gap: 10px;
        margin-block: 27px;
        width: min(100%, 700px);
      }
      &__icon {
        &-settings {
          display: block;
          background-image: url("@/assets/img/settings-blue.svg");
          background-size: contain;
          background-repeat: no-repeat;
          width: 18px;
          height: 18px;
          padding: 0;
          margin: 0;
          float: right;
          cursor: pointer;
        }
      }
    }
  }
}
.text__violation {
  color: var(--warning-color);
  font-size: 12px;
  line-height: 14px;
  font-weight: 400;
  padding: 0.125rem;
}
@media screen and (max-width: 1400px) {
  .patient-profile__visits__table {
    display: block;
    &__head {
      display: none;
    }
    &__body {
      display: block;
    }
    &__row, &__row.canceled {
      display: grid;
      border: 1px solid #eaeff3;
      grid-template-columns: 1fr;
      grid-gap: 5px;
      margin: 0 0 30px 0;
      padding: 15px 0;
    }
    &__cell {
      display: flex;
      justify-content: space-between;
      gap: 5px 30px;
      padding: 2px 8px;
      text-align: right;
      &.align {
        flex-direction: column;
        text-align: left;
      }
      &.status {
        justify-content: flex-end;
      }
      &.service-name {
        text-align: left;
        ul {
          max-width: 700px;
        }
      }
      &.position-mob {
        justify-content: space-between;
      }
    }
    &__cell:before {
      content: attr(data-label);
      font-weight: 700;
      margin-right: 20px;
    }
  }
}
@media screen and (max-width: 668px) {
  .patient-profile {
    &__visits {
      &__filter {
        grid-template-columns: 1fr;
      }
      &__table {
        &__cell {
          &.service-name {
            flex-direction: column;
          }
        }
      }
    }
  }
}
</style>
